import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Search, Filter, FileText, User, Phone, Mail } from 'lucide-react'
import { useAdminAuth } from '../../contexts/AdminAuthContext'
import { apiService } from '../../services/apiService'

const CustomerSearch = () => {
  const { admin } = useAdminAuth()
  const [searchTerm, setSearchTerm] = useState('')
  const [customers, setCustomers] = useState([])
  const [filteredCustomers, setFilteredCustomers] = useState([])
  const [loading, setLoading] = useState(false)
  const [filters, setFilters] = useState({
    status: 'all',
    hasActiveLoans: 'all',
    riskLevel: 'all'
  })

  useEffect(() => {
    // Load all customers on component mount
    handleSearch('')
  }, [])

  useEffect(() => {
    // Apply filters when customers or filters change
    applyFilters()
  }, [customers, filters])

  const handleSearch = async (term = searchTerm) => {
    setLoading(true)
    try {
      const response = await apiService.searchCustomers(term)
      
      if (response.success) {
        setCustomers(response.data || [])
      } else {
        // Use mock data for demo
        setCustomers([
          {
            id: 1,
            client_id: 'CL001',
            username: 'john_kamau',
            email: 'john@example.com',
            phone_number: '+256700123456',
            status: 'active',
            last_submission: '2024-03-15',
            has_active_loans: true,
            risk_level: 'low',
            total_submissions: 12
          },
          {
            id: 2,
            client_id: 'CL002',
            username: 'mary_nakato',
            email: 'mary@example.com',
            phone_number: '+256700654321',
            status: 'active',
            last_submission: '2024-03-10',
            has_active_loans: false,
            risk_level: 'low',
            total_submissions: 8
          },
          {
            id: 3,
            client_id: 'CL003',
            username: 'david_musoke',
            email: 'david@example.com',
            phone_number: '+256700987654',
            status: 'active',
            last_submission: '2024-02-20',
            has_active_loans: true,
            risk_level: 'medium',
            total_submissions: 15
          },
          {
            id: 4,
            client_id: 'CL004',
            username: 'sarah_nakato',
            email: 'sarah@example.com',
            phone_number: '+256700456789',
            status: 'inactive',
            last_submission: '2024-01-15',
            has_active_loans: false,
            risk_level: 'high',
            total_submissions: 3
          }
        ])
      }
    } catch (error) {
      console.error('Search failed:', error)
    } finally {
      setLoading(false)
    }
  }

  const applyFilters = () => {
    let filtered = [...customers]

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(customer =>
        customer.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.client_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.email.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    // Filter by status
    if (filters.status !== 'all') {
      filtered = filtered.filter(customer => customer.status === filters.status)
    }

    // Filter by active loans
    if (filters.hasActiveLoans !== 'all') {
      const hasLoans = filters.hasActiveLoans === 'true'
      filtered = filtered.filter(customer => customer.has_active_loans === hasLoans)
    }

    // Filter by risk level
    if (filters.riskLevel !== 'all') {
      filtered = filtered.filter(customer => customer.risk_level === filters.riskLevel)
    }

    setFilteredCustomers(filtered)
  }

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }))
  }

  const getRiskBadgeColor = (riskLevel) => {
    switch (riskLevel) {
      case 'low':
        return 'bg-success-100 text-success-800'
      case 'medium':
        return 'bg-warning-100 text-warning-800'
      case 'high':
        return 'bg-danger-100 text-danger-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-success-100 text-success-800'
      case 'inactive':
        return 'bg-gray-100 text-gray-800'
      case 'suspended':
        return 'bg-danger-100 text-danger-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Customer Search</h1>
          <p className="text-gray-600 mt-2">
            Search and manage customers in your MG
          </p>
        </div>

        {/* Search and Filters */}
        <div className="card card-body mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Search Bar */}
            <div>
              <label htmlFor="search" className="form-label">
                Search Customers
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  id="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="form-input pl-10"
                  placeholder="Search by username, client ID, or email..."
                />
              </div>
              <button
                onClick={() => handleSearch()}
                disabled={loading}
                className="btn btn-primary mt-3"
              >
                {loading ? 'Searching...' : 'Search'}
              </button>
            </div>

            {/* Filters */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2 mb-3">
                <Filter className="h-5 w-5 text-gray-400" />
                <span className="font-medium text-gray-700">Filters</span>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="status" className="form-label text-sm">
                    Status
                  </label>
                  <select
                    id="status"
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="form-input"
                  >
                    <option value="all">All Statuses</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="hasActiveLoans" className="form-label text-sm">
                    Active Loans
                  </label>
                  <select
                    id="hasActiveLoans"
                    value={filters.hasActiveLoans}
                    onChange={(e) => handleFilterChange('hasActiveLoans', e.target.value)}
                    className="form-input"
                  >
                    <option value="all">All Customers</option>
                    <option value="true">Has Active Loans</option>
                    <option value="false">No Active Loans</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="riskLevel" className="form-label text-sm">
                    Risk Level
                  </label>
                  <select
                    id="riskLevel"
                    value={filters.riskLevel}
                    onChange={(e) => handleFilterChange('riskLevel', e.target.value)}
                    className="form-input"
                  >
                    <option value="all">All Risk Levels</option>
                    <option value="low">Low Risk</option>
                    <option value="medium">Medium Risk</option>
                    <option value="high">High Risk</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Search Results ({filteredCustomers.length} customers)
              </h3>
              <div className="text-sm text-gray-600">
                Showing results for: {searchTerm || 'All customers'}
              </div>
            </div>
          </div>
          <div className="card-body p-0">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
                <p className="text-gray-600 mt-4">Searching customers...</p>
              </div>
            ) : filteredCustomers.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Customer
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contact
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Risk Level
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Last Submission
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredCustomers.map((customer) => (
                      <tr key={customer.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0">
                              <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                                <User className="h-6 w-6 text-primary-600" />
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {customer.username}
                              </div>
                              <div className="text-sm text-gray-500">
                                ID: {customer.client_id}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            <div className="flex items-center mb-1">
                              <Mail className="h-4 w-4 text-gray-400 mr-2" />
                              {customer.email || 'N/A'}
                            </div>
                            <div className="flex items-center">
                              <Phone className="h-4 w-4 text-gray-400 mr-2" />
                              {customer.phone_number || 'N/A'}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="space-y-2">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(customer.status)}`}>
                              {customer.status.charAt(0).toUpperCase() + customer.status.slice(1)}
                            </span>
                            {customer.has_active_loans && (
                              <div>
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                                  Has Loans
                                </span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRiskBadgeColor(customer.risk_level)}`}>
                            {customer.risk_level.charAt(0).toUpperCase() + customer.risk_level.slice(1)} Risk
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div>
                            {customer.last_submission ? 
                              new Date(customer.last_submission).toLocaleDateString() : 
                              'Never'
                            }
                          </div>
                          <div className="text-xs">
                            {customer.total_submissions} total submissions
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                          <Link
                            to={`/admin/financial-statements/${customer.id}`}
                            className="text-primary-600 hover:text-primary-900"
                          >
                            <FileText className="h-5 w-5 inline mr-1" />
                            View Statements
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center">
                <Search className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600">No customers found</p>
                <p className="text-sm text-gray-500 mt-2">
                  Try adjusting your search terms or filters
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Search Tips */}
        <div className="mt-8 bg-primary-50 rounded-lg p-6">
          <div className="flex items-start space-x-4">
            <div className="p-2 bg-primary-100 rounded-lg">
              <Search className="h-6 w-6 text-primary-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Search Tips</h3>
              <ul className="text-gray-600 space-y-1">
                <li>• Search by customer username, client ID, or email address</li>
                <li>• Use filters to narrow down results by status, loan status, or risk level</li>
                <li>• Click "View Statements" to see a customer's financial submission history</li>
                <li>• Leave the search field empty to see all customers in your MG</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CustomerSearch
